/**
 * The Ctrl+Shift+Alt chord that reveals AI internals to an admin.
 *
 * Pure: no React, no imports. CRA's jest cannot resolve react-router-dom v7, so
 * the only way to test logic that lives near components is to keep it in a
 * dependency-free module like this one and test it directly.
 *
 * WHY A TOGGLE AND NOT HOLD-TO-REVEAL. The whole point of the reveal is that an
 * admin can then CLICK the thing that appeared — a model chip through to its row
 * in pipeline settings. You cannot click while holding this chord: Ctrl+click is
 * a right-click on macOS and "open in new tab" on Windows and Linux. So the
 * chord flips a sticky mode instead, and Ctrl+Shift+Alt earns that job precisely
 * because nobody presses it by accident.
 *
 * The `armed` flag is what makes "nobody presses it by accident" true. The chord
 * only counts when the three modifiers are held and NOTHING else was pressed
 * with them — so Ctrl+Shift+Alt+I (and every other OS or browser chord that
 * happens to pass through) cancels instead of toggling.
 */

/** The three keys that make up the chord. Nothing else may join them. */
const CHORD_KEYS = new Set(['Control', 'Shift', 'Alt']);

export function isChordKey(key: string): boolean {
  return CHORD_KEYS.has(key);
}

export type ChordEvent =
  | { type: 'keydown'; key: string; ctrl: boolean; shift: boolean; alt: boolean }
  | { type: 'keyup'; key: string }
  /* A modified click is a click, not a chord — and on macOS Ctrl+click opens a
     context menu, which would otherwise toggle the mode behind the menu. */
  | { type: 'mousedown' }
  /* keyup is never delivered if focus leaves the window mid-chord, so a blur has
     to disarm or the next stray keyup would toggle. */
  | { type: 'blur' };

export interface ChordState {
  /** All three modifiers are down and no other key has interrupted them. */
  armed: boolean;
}

export const initialChordState: ChordState = { armed: false };

export interface ChordResult {
  state: ChordState;
  /** True on the one event that completes the chord. */
  toggle: boolean;
}

export function reduceChord(state: ChordState, event: ChordEvent): ChordResult {
  switch (event.type) {
    case 'keydown':
      // Any non-modifier key means this is some other shortcut. Cancel rather
      // than ignore: Ctrl+Shift+Alt+I must not leave a chord half-completed that
      // the release of Alt would then fire.
      if (!isChordKey(event.key)) return { state: initialChordState, toggle: false };
      return {
        state: { armed: event.ctrl && event.shift && event.alt },
        toggle: false,
      };

    case 'keyup':
      if (!state.armed) return { state, toggle: false };
      if (!isChordKey(event.key)) return { state: initialChordState, toggle: false };
      // Releasing any of the three completes it — waiting for all three to come
      // up would make the gesture depend on the order fingers happen to lift.
      return { state: initialChordState, toggle: true };

    case 'mousedown':
    case 'blur':
      return { state: initialChordState, toggle: false };

    default:
      return { state, toggle: false };
  }
}
